import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { Formik, Form, Field } from 'formik';
import isEmpty from 'lodash/isEmpty';
import { useTranslation } from 'react-i18next';
import makeStyles from '@mui/styles/makeStyles';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import TextField from '@mui/material/TextField';
import FormHelperText from '@mui/material/FormHelperText';

import { Alert, DialogTitleWithClose, Loader } from 'ui';
import { changePasswordSchema } from 'shared/validations';
import { changePasswordService } from 'services/login/changePasswordService';
import { addNotificationAction } from 'store/notifications/notificationsActions';

const useStyles = makeStyles(theme => ({
  alertWrapper: {
    padding: '0 16px',
  },
  helperText: {
    display: 'initial',
    color: theme.palette.error.dark,
  },
}));

const ChangePasswordModal = ({ open, onClose }) => {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState(false);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const classes = useStyles();

  const initialValues = {
    oldPassword: '',
    newPassword: '',
    confirmNewPassword: '',
  };

  const handleSubmit = values => {
    setIsPending(true);
    setError(false);

    changePasswordService(values)
      .then(() => {
        onClose();
        setError(false);
        dispatch(
          addNotificationAction({
            message: 'Password successfully changed',
            variant: 'success',
          }),
        );
      })
      .catch(() => setError(true))
      .finally(() => {
        setIsPending(false);
      });
  };

  const handleClose = () => {
    onClose();
    setError(false);
  };

  return (
    <Dialog open={open} onClose={handleClose}>
      {isPending && <Loader cover />}
      <DialogTitleWithClose onClose={handleClose}>{t('Change password')}</DialogTitleWithClose>
      {error && (
        <div className={classes.alertWrapper}>
          <Alert message={t('The current password is not correct.')} variant="error" />
        </div>
      )}
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={changePasswordSchema}
      >
        {({ errors, touched }) => (
          <Form noValidate>
            <DialogContent>
              <Field name="oldPassword">
                {({ field }) => (
                  <>
                    <TextField
                      {...field}
                      type="password"
                      label="Current password"
                      error={errors.oldPassword && touched.oldPassword}
                      fullWidth
                    />
                    {touched.oldPassword && errors.oldPassword && (
                      <FormHelperText className={classes.helperText}>
                        {errors.oldPassword}
                      </FormHelperText>
                    )}
                  </>
                )}
              </Field>
              <Field name="newPassword">
                {({ field }) => (
                  <>
                    <TextField
                      {...field}
                      type="password"
                      label="New password"
                      error={errors.newPassword && touched.newPassword}
                      fullWidth
                    />
                    {touched.newPassword && errors.newPassword && (
                      <FormHelperText className={classes.helperText}>
                        {errors.newPassword}
                      </FormHelperText>
                    )}
                  </>
                )}
              </Field>
              <Field name="confirmNewPassword">
                {({ field }) => (
                  <>
                    <TextField
                      {...field}
                      type="password"
                      label="Confirm new password"
                      error={errors.confirmNewPassword && touched.confirmNewPassword}
                      fullWidth
                    />
                    {touched.confirmNewPassword && errors.confirmNewPassword && (
                      <FormHelperText className={classes.helperText}>
                        {errors.confirmNewPassword}
                      </FormHelperText>
                    )}
                  </>
                )}
              </Field>
            </DialogContent>
            <DialogActions>
              <Button variant="outlined" onClick={handleClose}>
                {t('Cancel')}
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isPending || !isEmpty(errors) || isEmpty(touched)}
              >
                {t('Change password')}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

ChangePasswordModal.defaultProps = {
  open: false,
  onClose: () => null,
};

ChangePasswordModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
};

export default ChangePasswordModal;
